import type {ComponentProps} from "react";
import {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router";
import type {AssetEntry, GroupRepo, RecipeEntry} from "../../models/GroupRepo";
import type {UserRepo} from "../../models/UserRepo";
import {Box, Col, css, Row} from "../../ui/Box";
import Button from "../../ui/Button";
import {buttonStyles} from "../../ui/Button.css";
import cx from "../../utils/cx";
import {uploadFileLocalOrRemote} from "../../utils/file-helpers";
import {useDrag} from "../../utils/list-dnd/use-drag";

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

const BOX_WIDTH = 200;
const RESULT_WIDTH = 360;

const getBlob = async (canvas: HTMLCanvasElement, type: string) => {
  return new Promise<Blob | null>((res) => {
    canvas.toBlob((blob) => {
      if (blob) {
        res(blob);
      } else {
        res(null);
      }
    }, type);
  });
};

const replaceExtension = (filename: string, ext: string) => {
  const lastDotIndex = filename.lastIndexOf(".");
  const baseName = lastDotIndex === -1 ? filename : filename.slice(0, lastDotIndex);
  return baseName.slice(0, 32) + "." + ext;
};

const getBestBlob = async (canvas: HTMLCanvasElement, fileName: string) => {
  const types = ["image/jpeg", "image/png", "image/webp"];
  let best = null as null | [Blob, number, string];
  for (const type of types) {
    const blob = await getBlob(canvas, type);
    if (!blob) continue;
    if (!best || blob.size < best[0].size) {
      best = [blob, blob.size, type];
    }
  }
  const extension = best![2].split("/")[1];
  const type = best![2];
  const file = new File([best![0]], replaceExtension(fileName, extension), {type});
  return {file, type};
};

const PreviewImage = ({
  url,
  onSave,
  fileName,
}: {
  url: string;
  onSave: (fileWithMeta: FileWithMeta) => Promise<unknown>;
  fileName: string;
}) => {
  const [imgInfo, setImgInfo] = useState<{width: number; height: number} | null>(null);
  const offsetRef = useRef(0);
  const isHorizontal = imgInfo && imgInfo.width / imgInfo.height > 16 / 9;
  const handlers = useDrag(({action, movement: [mx, my]}) => {
    const getLimits = () => {
      const boxHeight = BOX_WIDTH / (16 / 9);
      if (isHorizontal) {
        const imgWidth = (boxHeight * imgInfo.width) / imgInfo.height;
        return imgWidth / 2 - BOX_WIDTH / 2;
      } else {
        const imgHeight = (BOX_WIDTH * imgInfo!.height) / imgInfo!.width;
        return imgHeight / 2 - boxHeight / 2;
      }
    };
    const prop = isHorizontal ? "left" : "top";
    const val = isHorizontal ? mx : my;
    const limit = getLimits();
    imgNodeRef.current!.style[prop] = `${clamp(offsetRef.current + val, -limit, limit)}px`;
    if (action === "end") {
      offsetRef.current = clamp(offsetRef.current + val, -limit, limit);
    }
  });

  const handleSave = async () => {
    const canvas = document.createElement("canvas");
    const resultHeight = Math.round(RESULT_WIDTH / (16 / 9));
    canvas.width = RESULT_WIDTH;
    canvas.height = resultHeight;
    const ctx = canvas.getContext("2d")!;
    const imgNode = imgNodeRef.current!;
    const getArgs = (): [number, number, number, number] => {
      const boxHeight = BOX_WIDTH / (16 / 9);
      if (isHorizontal) {
        const resultHeight = RESULT_WIDTH / (16 / 9);
        const imgWidth = (boxHeight * imgInfo.width) / imgInfo.height;
        const offsetPixels = imgWidth / 2 - BOX_WIDTH / 2 - offsetRef.current;
        return [
          Math.round(-offsetPixels * (RESULT_WIDTH / BOX_WIDTH)),
          0,
          Math.round(resultHeight / (imgInfo.height / imgInfo.width)),
          Math.round(resultHeight),
        ];
      } else {
        const imgHeight = (BOX_WIDTH * imgInfo!.height) / imgInfo!.width;
        const offsetPixels = imgHeight / 2 - boxHeight / 2 - offsetRef.current;
        return [
          0,
          Math.round(-(offsetPixels * (RESULT_WIDTH / BOX_WIDTH))),
          RESULT_WIDTH,
          Math.round(RESULT_WIDTH * (imgInfo!.height / imgInfo!.width)),
        ];
      }
    };

    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(imgNode, ...getArgs());
    const {file, type} = await getBestBlob(canvas, fileName);
    await onSave({file, meta: {width: RESULT_WIDTH, height: resultHeight, mimeType: type}});
  };

  const imgNodeRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = imgNodeRef.current!;
    setImgInfo({width: img.naturalWidth, height: img.naturalHeight});
    offsetRef.current = 0;
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      // wait until image is loaded
      img.addEventListener("load", () => {
        setImgInfo({width: img.naturalWidth, height: img.naturalHeight});
        offsetRef.current = 0;
      });
    }
  }, []);

  return (
    <>
      <Box
        bg="background"
        pa="16"
        rounded="4"
        relative
        {...handlers}
        style={{touchAction: "none"}}
        overflow="hidden"
        cursor="grab"
        aspectRatio="16 / 9"
      >
        <Col absolute inset="0" align="center" justify="center">
          <img
            src={url}
            alt="Preview"
            style={
              isHorizontal
                ? {height: BOX_WIDTH / (16 / 9), maxWidth: "initial"}
                : {width: BOX_WIDTH}
            }
            draggable={false}
            ref={imgNodeRef}
            className={css({userSelect: "none", touchAction: "none", position: "relative"})}
          />
        </Col>
        <Col absolute inset="0" align="center" justify="center">
          <Box
            rounded="4"
            borderWidth={1}
            borderColor="default"
            aspectRatio="16 / 9"
            style={{
              width: BOX_WIDTH,
              boxShadow: "0 0 0 1000px rgba(0,0,0,0.5)",
            }}
          ></Box>
        </Col>
      </Box>
      <Button variant="primary" onClick={handleSave}>
        Save
      </Button>
    </>
  );
};

const UploadButton = ({
  label,
  ...rest
}: {label: string} & Pick<ComponentProps<"input">, "accept" | "capture" | "onChange">) => (
  <label className={cx(buttonStyles.base, buttonStyles.size.md, buttonStyles.variant.primary)}>
    {label}
    <input type="file" {...rest} className={css({display: "none"})} />
  </label>
);

const UploadArea = ({
  onSave,
}: {
  onSave: (fileWithMeta: FileWithMeta | null) => Promise<unknown>;
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
        setFileName(file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  return previewUrl && fileName !== null ? (
    <PreviewImage url={previewUrl} key={previewUrl} onSave={onSave} fileName={fileName} />
  ) : (
    <Row sp="8">
      <UploadButton accept="image/*" onChange={handleChange} label="Pick from Gallery" />
      <UploadButton capture="environment" onChange={handleChange} label="Camera" />
    </Row>
  );
};

type FileWithMeta = {file: File; meta: AssetEntry["meta"]};

const UploadImage = ({
  userRepo,
  groupRepo,
  recipe,
}: {
  userRepo: UserRepo;
  groupRepo: GroupRepo;
  recipe: RecipeEntry;
}) => {
  const group = groupRepo.vals.useValues();
  const navigate = useNavigate();
  const onClose = () => navigate("..");
  const handleSave = async (fileWithMeta: FileWithMeta | null) => {
    if (fileWithMeta) {
      await uploadFileLocalOrRemote({
        file: fileWithMeta.file,
        meta: fileWithMeta.meta,
        groupId: group.id,
        groupRepo,
        userRepo,
        withAssetId: (assetId) => {
          groupRepo.Recipes.patch(recipe.id, {coverImage: assetId});
        },
      });
      onClose();
    } else {
      if (recipe.coverImage) {
        groupRepo.Recipes.patch(recipe.id, {coverImage: null});
      }
      onClose();
    }
  };
  return (
    <Col sp="24" maxWidth="formWidth" width="100%" mr="auto" ml="auto" py="12">
      <UploadArea onSave={handleSave} />
    </Col>
  );
};

export default UploadImage;
